import { useQueries, useQuery } from '@tanstack/react-query';
import {
  type BoardFaceList,
  type BoardMember,
  type CardDetail,
  type Checklist,
  type ChecklistItem,
} from '@molt/shared';
import {
  ArrowDown,
  ArrowUp,
  AtSign,
  CalendarClock,
  CreditCard,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import {
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  Avatar,
  Button,
  Combobox,
  Dialog,
  EmptyState,
  IconButton,
  InlineEdit,
  TextField,
  profileName,
} from '@/components';
import { boardContentsQueryOptions } from '@/lib/board-face';
import {
  dateTimeLocalToIso,
  dueStatus,
  dueStatusLabel,
  formatCardDate,
  formatDateTimeLocal,
} from '@/lib/card-dates';
import {
  checklistProgress,
  getCardChecklists,
  mentionSegments,
  useAddChecklistItems,
  useConvertChecklistItemToCard,
  useCreateChecklist,
  useDeleteChecklist,
  useDeleteChecklistItem,
  useMoveChecklist,
  useMoveChecklistItem,
  useRenameChecklist,
  useRenameChecklistItem,
  useSetChecklistItemAssignee,
  useSetChecklistHideChecked,
  useSetChecklistItemDueDate,
  useSetChecklistItemChecked,
  visibleChecklistItems,
  type ChecklistTranslate,
} from '@/lib/checklists';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/cn';

interface ChecklistConvertListOption {
  readonly id: string;
  readonly title: string;
}

export interface CardChecklistSectionProps {
  card: CardDetail;
  boardId: string;
  canEdit: boolean;
  boardMembers: readonly BoardMember[];
  currentUserId?: string | undefined;
}

interface ChecklistSourceOption {
  readonly id: string;
  readonly checklistTitle: string;
  readonly cardTitle: string;
  readonly itemCount: number;
}

export function CardChecklistSection({
  card,
  boardId,
  canEdit,
  boardMembers,
  currentUserId,
}: CardChecklistSectionProps): ReactElement {
  const [addOpen, setAddOpen] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourceChecklistId, setSourceChecklistId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Checklist | null>(null);

  const { t } = useInterfaceLanguage();
  const createChecklist = useCreateChecklist(t);
  const moveChecklist = useMoveChecklist(t);
  const deleteChecklist = useDeleteChecklist(t);

  const boardContents = useQuery({
    ...boardContentsQueryOptions(boardId),
    enabled: canEdit || addOpen,
  });

  const candidateCards = useMemo(
    () =>
      boardCards(boardContents.data?.lists ?? []).filter(
        (candidate) => candidate.id !== card.id,
      ),
    [boardContents.data?.lists, card.id],
  );

  const checklistConvertLists = useMemo(() => {
    const open =
      boardContents.data?.lists
        .filter((list) => !list.archived)
        .map((list) => ({ id: list.id, title: list.title })) ?? [];
    const hasCurrent = open.some((list) => list.id === card.listId);
    if (hasCurrent) return open;
    return [{ id: card.listId, title: card.list.title }, ...open];
  }, [boardContents.data?.lists, card.list.title, card.listId]);

  const candidateChecklistQueries = useQueries({
    queries: addOpen
      ? candidateCards.map((candidate) => ({
          queryKey: queryKeys.cards.checklists(candidate.id),
          queryFn: () => getCardChecklists(candidate.id),
        }))
      : [],
  });

  const sourceOptions = useMemo(() => {
    const options: ChecklistSourceOption[] = [
      ...card.checklists.map((checklist) => ({
        id: checklist.id,
        checklistTitle: checklist.title,
        cardTitle: card.title,
        itemCount: checklist.items.length,
      })),
    ];
    candidateChecklistQueries.forEach((query, index) => {
      const candidate = candidateCards[index];
      if (candidate === undefined || query.data === undefined) return;
      options.push(
        ...query.data.map((checklist) => ({
          id: checklist.id,
          checklistTitle: checklist.title,
          cardTitle: candidate.title,
          itemCount: checklist.items.length,
        })),
      );
    });
    return options;
  }, [candidateCards, candidateChecklistQueries, card.checklists, card.title]);

  const filteredSourceOptions = useMemo(() => {
    const query = sourceSearch.trim().toLocaleLowerCase();
    if (query === '') return sourceOptions;
    return sourceOptions.filter((option) =>
      [option.checklistTitle, option.cardTitle]
        .join(' ')
        .toLocaleLowerCase()
        .includes(query),
    );
  }, [sourceOptions, sourceSearch]);

  async function submitChecklist(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const title = titleDraft.trim();
    if (title.length === 0) return;
    await createChecklist.mutateAsync({
      cardId: card.id,
      boardId,
      title,
      sourceChecklistId,
    });
    setTitleDraft('');
    setSourceSearch('');
    setSourceChecklistId(null);
    setAddOpen(false);
  }

  function closeAddDialog(open: boolean) {
    setAddOpen(open);
    if (open) return;
    setTitleDraft('');
    setSourceSearch('');
    setSourceChecklistId(null);
  }

  return (
    <section aria-labelledby="card-checklists-heading" className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <h2 id="card-checklists-heading" className="text-sm font-semibold text-ink">
          {t('cardBack.checklists.heading')}
        </h2>
        <div className="min-w-0 flex-1" />
        {canEdit ? (
          <Button
            size="sm"
            variant="secondary"
            iconStart={<Plus aria-hidden="true" className="size-4" />}
            aria-label={t('cardBack.checklists.addTrigger', { card: card.title })}
            onClick={() => setAddOpen(true)}
          >
            {t('cardBack.checklists.addTriggerLabel')}
          </Button>
        ) : null}
      </div>

      {card.checklists.length === 0 ? (
        <EmptyState
          tone="neutral"
          size="inline"
          title={t('cardBack.checklists.emptyTitle')}
          body={canEdit ? t('cardBack.checklists.emptyBody') : undefined}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {card.checklists.map((checklist) => (
            <ChecklistPanel
              key={checklist.id}
              t={t}
              checklist={checklist}
              checklists={card.checklists}
              cardId={card.id}
              boardId={boardId}
              canEdit={canEdit}
              people={boardMembers}
              currentUserId={currentUserId}
              currentListId={card.listId}
              convertListOptions={checklistConvertLists}
              onMoveChecklist={(direction) => {
                const neighbours = checklistMoveNeighbours(
                  card.checklists,
                  checklist.id,
                  direction,
                );
                if (neighbours === null) return;
                moveChecklist.mutate({
                  cardId: card.id,
                  boardId,
                  checklistId: checklist.id,
                  previousChecklistId: neighbours.previousChecklistId,
                  nextChecklistId: neighbours.nextChecklistId,
                });
              }}
              onDelete={() => setDeleteTarget(checklist)}
            />
          ))}
        </div>
      )}

      <Dialog
        open={addOpen}
        onOpenChange={closeAddDialog}
        size="sm"
        title={t('cardBack.checklists.add.title')}
        closeControl={
          <IconButton label={t('cardBack.checklists.add.close')} size="sm">
            <X aria-hidden="true" className="size-4" />
          </IconButton>
        }
        footer={
          <>
            <Button size="sm" variant="quiet" onClick={() => closeAddDialog(false)}>
              {t('cardBack.checklists.cancel')}
            </Button>
            <Button
              size="sm"
              variant="primary"
              type="submit"
              form="card-checklist-add-form"
              busy={createChecklist.isPending}
              busyLabel={t('cardBack.checklists.add.busy')}
              disabled={titleDraft.trim().length === 0}
            >
              {t('cardBack.checklists.add.submit')}
            </Button>
          </>
        }
      >
        <form
          id="card-checklist-add-form"
          className="flex flex-col gap-3"
          onSubmit={(event) => void submitChecklist(event)}
        >
          <TextField
            label={t('cardBack.checklists.add.titleLabel')}
            value={titleDraft}
            onChange={(event) => setTitleDraft(event.currentTarget.value)}
          />
          <div className="flex flex-col gap-2">
            <TextField
              label={t('cardBack.checklists.add.searchLabel')}
              value={sourceSearch}
              onChange={(event) => setSourceSearch(event.currentTarget.value)}
            />
            <label className="flex flex-col gap-1 text-sm font-medium text-ink">
              {t('cardBack.checklists.add.copyFromLabel')}
              <select
                className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-card text-ink"
                value={sourceChecklistId ?? ''}
                onChange={(event) =>
                  setSourceChecklistId(event.currentTarget.value || null)
                }
              >
                <option value="">{t('cardBack.checklists.add.noSource')}</option>
                {filteredSourceOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {t(
                      'cardBack.checklists.add.sourceOption',
                      {
                        checklist: option.checklistTitle,
                        card: option.cardTitle,
                        count: option.itemCount,
                      },
                      option.itemCount,
                    )}
                  </option>
                ))}
              </select>
            </label>
            {sourceOptions.length === 0 ? (
              <p className="text-sm text-ink-muted">
                {t('cardBack.checklists.add.noSourcesAvailable')}
              </p>
            ) : null}
          </div>
        </form>
      </Dialog>

      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        size="sm"
        title={
          deleteTarget === null
            ? t('cardBack.checklists.delete.title')
            : t('cardBack.checklists.delete.titleNamed', {
                checklist: deleteTarget.title,
              })
        }
        description={t('cardBack.checklists.delete.description')}
        footer={
          <>
            <Button size="sm" variant="quiet" onClick={() => setDeleteTarget(null)}>
              {t('cardBack.checklists.cancel')}
            </Button>
            <Button
              size="sm"
              variant="danger"
              busy={deleteChecklist.isPending}
              onClick={() => {
                if (deleteTarget === null) return;
                deleteChecklist.mutate(
                  {
                    cardId: card.id,
                    boardId,
                    checklistId: deleteTarget.id,
                    title: deleteTarget.title,
                  },
                  { onSuccess: () => setDeleteTarget(null) },
                );
              }}
            >
              {t('cardBack.checklists.delete.confirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-muted">
          {t('cardBack.checklists.delete.undoNotice')}
        </p>
      </Dialog>
    </section>
  );
}

function ChecklistPanel({
  t,
  checklist,
  checklists,
  cardId,
  boardId,
  canEdit,
  people,
  currentUserId,
  currentListId,
  convertListOptions,
  onMoveChecklist,
  onDelete,
}: {
  t: ChecklistTranslate;
  checklist: Checklist;
  checklists: readonly Checklist[];
  cardId: string;
  boardId: string;
  canEdit: boolean;
  people: readonly BoardMember[];
  currentUserId: string | undefined;
  currentListId: string;
  convertListOptions: readonly ChecklistConvertListOption[];
  onMoveChecklist: (direction: MoveDirection) => void;
  onDelete: () => void;
}): ReactElement {
  const renameChecklist = useRenameChecklist(t);
  const setHideChecked = useSetChecklistHideChecked(t);
  const addItems = useAddChecklistItems(t);
  const renameItem = useRenameChecklistItem(t);
  const setItemChecked = useSetChecklistItemChecked(t);
  const moveItem = useMoveChecklistItem(t);
  const deleteItem = useDeleteChecklistItem(t);
  const assignItem = useSetChecklistItemAssignee(t);
  const setItemDueDate = useSetChecklistItemDueDate(t);
  const convertItem = useConvertChecklistItemToCard(t);
  const [itemDraft, setItemDraft] = useState('');
  const [convertTarget, setConvertTarget] = useState<ChecklistItem | null>(null);
  const [convertListId, setConvertListId] = useState(currentListId);
  const addItemRef = useRef<HTMLTextAreaElement | null>(null);
  const progress = checklistProgress(checklist);
  const visibleItems = visibleChecklistItems(checklist);
  const headingId = `checklist-${checklist.id}-heading`;
  const checklistIndex = checklists.findIndex(
    (candidate) => candidate.id === checklist.id,
  );
  const addItemMentionQuery = activeMentionQuery(itemDraft);
  const addItemMentionOptions = mentionOptions(people, addItemMentionQuery);

  async function submitItems() {
    const text = itemDraft;
    if (text.trim().length === 0) return;
    await addItems.mutateAsync({ cardId, boardId, checklistId: checklist.id, text });
    setItemDraft('');
    window.setTimeout(() => addItemRef.current?.focus(), 0);
  }

  function handleItemKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    void submitItems();
  }

  function moveItemToChecklist(item: ChecklistItem, checklistId: string) {
    if (item.checklistId === checklistId) return;
    const destination = checklists.find((candidate) => candidate.id === checklistId);
    if (destination === undefined) return;
    moveItem.mutate({
      cardId,
      boardId,
      itemId: item.id,
      checklistId: destination.id,
      previousItemId: destination.items.at(-1)?.id ?? null,
      nextItemId: null,
    });
  }

  function moveItemBefore(item: ChecklistItem, target: ChecklistItem) {
    if (item.id === target.id) return;
    const destinationItems = checklist.items.filter(
      (candidate) => candidate.id !== item.id,
    );
    const targetIndex = destinationItems.findIndex(
      (candidate) => candidate.id === target.id,
    );
    if (targetIndex === -1) return;
    moveItem.mutate({
      cardId,
      boardId,
      itemId: item.id,
      checklistId: checklist.id,
      previousItemId: destinationItems[targetIndex - 1]?.id ?? null,
      nextItemId: target.id,
    });
  }

  function findChecklistItem(itemId: string): ChecklistItem | undefined {
    return checklists
      .flatMap((candidate) => candidate.items)
      .find((candidate) => candidate.id === itemId);
  }

  function handleItemDrop(event: DragEvent<HTMLElement>) {
    if (!canEdit) return;
    event.preventDefault();
    const itemId = event.dataTransfer.getData('application/x-molt-checklist-item');
    const draggedItem = findChecklistItem(itemId);
    if (draggedItem === undefined) return;
    moveItemToChecklist(draggedItem, checklist.id);
  }

  return (
    <article
      aria-labelledby={headingId}
      className="flex flex-col gap-3 rounded-card border border-border bg-surface-raised p-3"
      onDragOver={(event) => {
        if (canEdit) event.preventDefault();
      }}
      onDrop={handleItemDrop}
    >
      <div className="flex items-start gap-2">
        <div id={headingId} className="min-w-0 flex-1">
          <InlineEdit
            value={checklist.title}
            label={t('cardBack.checklists.panel.titleLabel')}
            readOnly={!canEdit}
            onCommit={async (title) => {
              await renameChecklist.mutateAsync({
                cardId,
                boardId,
                checklistId: checklist.id,
                title,
              });
            }}
            controlClassName="text-sm font-semibold text-ink"
          />
        </div>
        {canEdit ? (
          <div className="flex shrink-0 items-center gap-1">
            <IconButton
              label={t('cardBack.checklists.panel.moveUp', {
                checklist: checklist.title,
              })}
              size="sm"
              disabled={checklistIndex <= 0}
              onClick={() => onMoveChecklist('up')}
            >
              <ArrowUp aria-hidden="true" className="size-4" />
            </IconButton>
            <IconButton
              label={t('cardBack.checklists.panel.moveDown', {
                checklist: checklist.title,
              })}
              size="sm"
              disabled={checklistIndex === -1 || checklistIndex >= checklists.length - 1}
              onClick={() => onMoveChecklist('down')}
            >
              <ArrowDown aria-hidden="true" className="size-4" />
            </IconButton>
            <IconButton
              label={t('cardBack.checklists.panel.delete', {
                checklist: checklist.title,
              })}
              size="sm"
              onClick={onDelete}
            >
              <Trash2 aria-hidden="true" className="size-4" />
            </IconButton>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 text-sm text-ink-muted">
          <span className="font-medium text-ink">
            {progress.checked}/{progress.total}
          </span>
          <span>
            {t(
              'cardBack.checklists.panel.progressSuffix',
              { count: progress.total },
              progress.total,
            )}
          </span>
        </div>
        <div
          role="progressbar"
          aria-label={t('cardBack.checklists.panel.progressBar', {
            checklist: checklist.title,
          })}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress.percent}
          className="h-2 overflow-hidden rounded-full bg-surface"
        >
          <div
            className={cn('h-full bg-accent', progress.complete ? 'bg-ok' : undefined)}
            style={{ width: `${String(progress.percent)}%` }}
          />
        </div>
      </div>

      {progress.checked > 0 ? (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="toggle"
            pressed={checklist.hideChecked}
            aria-label={
              checklist.hideChecked
                ? t('cardBack.checklists.panel.showCheckedIn', {
                    checklist: checklist.title,
                  })
                : t('cardBack.checklists.panel.hideCheckedIn', {
                    checklist: checklist.title,
                  })
            }
            onClick={() =>
              setHideChecked.mutate({
                cardId,
                boardId,
                checklistId: checklist.id,
                hidden: !checklist.hideChecked,
              })
            }
          >
            {checklist.hideChecked
              ? t('cardBack.checklists.panel.showChecked')
              : t('cardBack.checklists.panel.hideChecked')}
          </Button>
          {checklist.hideChecked ? (
            <span className="text-sm text-ink-muted">
              {t(
                'cardBack.checklists.panel.hiddenCount',
                { count: progress.hidden },
                progress.hidden,
              )}
            </span>
          ) : null}
        </div>
      ) : null}

      {visibleItems.length === 0 ? (
        <p className="text-sm text-ink-muted">
          {checklist.items.length === 0
            ? t('cardBack.checklists.panel.noItems')
            : t('cardBack.checklists.panel.allHidden')}
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {visibleItems.map((item) => (
            <ChecklistItemRow
              key={item.id}
              t={t}
              item={item}
              cardId={cardId}
              boardId={boardId}
              canEdit={canEdit}
              people={people}
              currentUserId={currentUserId}
              onRename={async (text) => {
                await renameItem.mutateAsync({ cardId, boardId, itemId: item.id, text });
              }}
              onAssign={(assigneeUserId) =>
                assignItem.mutate({
                  cardId,
                  boardId,
                  itemId: item.id,
                  assigneeUserId,
                })
              }
              onSetDueDate={(dueAt) =>
                setItemDueDate.mutate({
                  cardId,
                  boardId,
                  itemId: item.id,
                  dueAt,
                })
              }
              onConvert={() => {
                setConvertTarget(item);
                setConvertListId(currentListId);
              }}
              onChecked={(checked) =>
                setItemChecked.mutate({
                  cardId,
                  boardId,
                  itemId: item.id,
                  checked,
                })
              }
              onDelete={() => deleteItem.mutate({ cardId, boardId, itemId: item.id })}
              onMoveWithin={(direction) => {
                const neighbours = checklistItemMoveNeighbours(
                  checklist.items,
                  item.id,
                  direction,
                );
                if (neighbours === null) return;
                moveItem.mutate({
                  cardId,
                  boardId,
                  itemId: item.id,
                  checklistId: checklist.id,
                  previousItemId: neighbours.previousItemId,
                  nextItemId: neighbours.nextItemId,
                });
              }}
              onMoveToChecklist={(nextChecklistId) => {
                moveItemToChecklist(item, nextChecklistId);
              }}
              onDropItem={(draggedItemId) => {
                const draggedItem = findChecklistItem(draggedItemId);
                if (draggedItem === undefined) return;
                moveItemBefore(draggedItem, item);
              }}
              checklistOptions={checklists.map((candidate) => ({
                id: candidate.id,
                title: candidate.title,
              }))}
            />
          ))}
        </ul>
      )}

      {canEdit ? (
        <div className="flex flex-col gap-2">
          <TextField
            ref={addItemRef}
            label={t('cardBack.checklists.panel.addItemLabel', {
              checklist: checklist.title,
            })}
            multiline
            value={itemDraft}
            onChange={(event) => setItemDraft(event.currentTarget.value)}
            onKeyDown={handleItemKeyDown}
          />
          {addItemMentionQuery !== null ? (
            <Combobox
              label={t('cardBack.checklists.mention.newItemLabel')}
              hideLabel
              autoFocus
              value={addItemMentionQuery}
              onChange={(value) =>
                setItemDraft(replaceActiveMention(itemDraft, value, false))
              }
              minLength={2}
              leadingIcon={<AtSign aria-hidden="true" className="size-4" />}
              groups={[
                {
                  id: 'board-members',
                  label: t('cardBack.checklists.mention.group'),
                  options: addItemMentionOptions,
                },
              ]}
              emptyState={
                addItemMentionQuery.length < 2 ? (
                  <EmptyState
                    tone="neutral"
                    size="inline"
                    title={t('cardBack.checklists.mention.typeTwo')}
                  />
                ) : (
                  <EmptyState
                    tone="neutral"
                    size="inline"
                    title={t('cardBack.checklists.mention.noMatch')}
                  />
                )
              }
              onActivate={(option) => {
                setItemDraft(replaceActiveMention(itemDraft, option.username, true));
                window.setTimeout(() => addItemRef.current?.focus(), 0);
              }}
              renderOption={(option) => <MentionOption option={option} />}
            />
          ) : null}
          <div>
            <Button
              size="sm"
              variant="secondary"
              busy={addItems.isPending}
              disabled={itemDraft.trim().length === 0}
              onClick={() => void submitItems()}
            >
              {t('cardBack.checklists.panel.addItem')}
            </Button>
          </div>
        </div>
      ) : null}

      <Dialog
        open={convertTarget !== null}
        onOpenChange={(open) => {
          if (!open) setConvertTarget(null);
        }}
        size="sm"
        title={t('cardBack.checklists.convert.title')}
        closeControl={
          <IconButton label={t('cardBack.checklists.convert.close')} size="sm">
            <X aria-hidden="true" className="size-4" />
          </IconButton>
        }
        footer={
          <>
            <Button size="sm" variant="quiet" onClick={() => setConvertTarget(null)}>
              {t('cardBack.checklists.cancel')}
            </Button>
            <Button
              size="sm"
              variant="primary"
              busy={convertItem.isPending}
              onClick={() => {
                if (convertTarget === null) return;
                convertItem.mutate(
                  {
                    cardId,
                    boardId,
                    itemId: convertTarget.id,
                    listId: convertListId === currentListId ? null : convertListId,
                  },
                  { onSuccess: () => setConvertTarget(null) },
                );
              }}
            >
              {t('cardBack.checklists.convert.confirm')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-ink-muted">
            {t('cardBack.checklists.convert.description')}
          </p>
          <label className="flex flex-col gap-1 text-sm font-medium text-ink">
            {t('cardBack.checklists.convert.listLabel')}
            <select
              className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-card text-ink"
              value={convertListId}
              onChange={(event) => setConvertListId(event.currentTarget.value)}
            >
              {convertListOptions.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.title}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Dialog>
    </article>
  );
}

function ChecklistItemRow({
  t,
  item,
  cardId: _cardId,
  boardId: _boardId,
  canEdit,
  people,
  currentUserId,
  onRename,
  onAssign,
  onSetDueDate,
  onConvert,
  onChecked,
  onDelete,
  onMoveWithin,
  onMoveToChecklist,
  onDropItem,
  checklistOptions,
}: {
  t: ChecklistTranslate;
  item: ChecklistItem;
  cardId: string;
  boardId: string;
  canEdit: boolean;
  people: readonly BoardMember[];
  currentUserId: string | undefined;
  onRename: (text: string) => Promise<void>;
  onAssign: (assigneeUserId: string | null) => void;
  onSetDueDate: (dueAt: string | null) => void;
  onConvert: () => void;
  onChecked: (checked: boolean) => void;
  onDelete: () => void;
  onMoveWithin: (direction: MoveDirection) => void;
  onMoveToChecklist: (checklistId: string) => void;
  onDropItem: (itemId: string) => void;
  checklistOptions: readonly ChecklistMoveOption[];
}): ReactElement {
  const assignee =
    item.assigneeUserId === null
      ? null
      : (people.find((person) => person.id === item.assigneeUserId) ?? null);
  const status = dueStatus({ dueAt: item.dueAt, completed: item.checked });
  const dueLabel = item.dueAt === null ? null : formatCardDate(t, item.dueAt);

  function handleDueDateChange(value: string) {
    if (value === '') {
      onSetDueDate(null);
      return;
    }
    try {
      onSetDueDate(dateTimeLocalToIso(value));
    } catch {
      onSetDueDate(null);
    }
  }

  return (
    <li
      className="flex flex-wrap items-start gap-2 rounded-card px-1 py-1"
      draggable={canEdit}
      onDragStart={(event) => {
        event.dataTransfer.setData('application/x-molt-checklist-item', item.id);
        event.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(event) => {
        if (canEdit) event.preventDefault();
      }}
      onDrop={(event) => {
        if (!canEdit) return;
        event.preventDefault();
        event.stopPropagation();
        onDropItem(event.dataTransfer.getData('application/x-molt-checklist-item'));
      }}
    >
      <input
        type="checkbox"
        className="mt-1 size-4 rounded border-border-strong text-accent"
        checked={item.checked}
        disabled={!canEdit}
        aria-label={item.text}
        onChange={(event) => onChecked(event.currentTarget.checked)}
      />
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col gap-1',
          item.checked ? 'text-ink-muted' : 'text-ink',
        )}
      >
        {item.convertedCard === null ? (
          <ChecklistItemTextEditor
            t={t}
            text={item.text}
            checked={item.checked}
            canEdit={canEdit}
            people={people}
            currentUserId={currentUserId}
            onCommit={onRename}
          />
        ) : (
          <a
            href={item.convertedCard.path}
            aria-label={t('cardBack.checklists.item.openConverted', {
              card: item.convertedCard.title,
            })}
            className={cn(
              'inline-flex min-w-0 items-center gap-1 rounded-card px-1 text-sm font-medium text-accent hover:underline',
              item.checked && 'line-through',
            )}
          >
            <CreditCard aria-hidden="true" className="size-4 shrink-0" />
            <span className="truncate">{item.convertedCard.title}</span>
          </a>
        )}
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-ink-muted">
          {assignee !== null ? (
            <span
              className="inline-flex max-w-full items-center gap-1 rounded-card border border-border bg-surface px-1.5 py-0.5"
              aria-label={t('cardBack.checklists.item.assignedTo', {
                name: profileName(assignee),
              })}
            >
              <Avatar profile={assignee} size="xs" decorative />
              <span className="truncate">{profileName(assignee)}</span>
            </span>
          ) : null}
          {dueLabel !== null ? (
            <span
              className={cn(
                'inline-flex max-w-full items-center gap-1 rounded-card border bg-surface px-1.5 py-0.5 font-medium',
                dueStateClasses(status),
              )}
              aria-label={t('cardBack.checklists.item.due', {
                due: dueLabel,
                status: dueStatusLabel(t, status),
              })}
            >
              <CalendarClock aria-hidden="true" className="size-3.5 shrink-0" />
              <span className="truncate">{dueLabel}</span>
              <span>{dueStatusLabel(t, status)}</span>
            </span>
          ) : null}
          {item.convertedCard !== null ? (
            <span className="inline-flex items-center gap-1 rounded-card bg-surface px-1.5 py-0.5">
              {t('cardBack.checklists.item.convertedBadge')}
            </span>
          ) : null}
        </div>
      </div>
      {canEdit ? (
        <div className="flex w-full flex-wrap items-center justify-start gap-1 pl-6 sm:max-w-72 sm:shrink-0 sm:justify-end sm:pl-0">
          <label className="sr-only" htmlFor={`checklist-item-${item.id}-assignee`}>
            {t('cardBack.checklists.item.assignLabel', { item: item.text })}
          </label>
          <select
            id={`checklist-item-${item.id}-assignee`}
            className="h-7 max-w-full rounded-card border border-border-strong bg-surface-raised px-2 text-sm text-ink sm:max-w-32"
            value={item.assigneeUserId ?? ''}
            onChange={(event) => onAssign(event.currentTarget.value || null)}
          >
            <option value="">{t('cardBack.checklists.item.noAssignee')}</option>
            {people.map((person) => (
              <option key={person.id} value={person.id}>
                {profileName(person)}
              </option>
            ))}
          </select>
          <label className="sr-only" htmlFor={`checklist-item-${item.id}-due-date`}>
            {t('cardBack.checklists.item.setDueLabel', { item: item.text })}
          </label>
          <input
            id={`checklist-item-${item.id}-due-date`}
            type="datetime-local"
            className="h-7 max-w-full rounded-card border border-border-strong bg-surface-raised px-2 text-sm text-ink sm:max-w-40"
            value={formatDateTimeLocal(item.dueAt)}
            onChange={(event) => handleDueDateChange(event.currentTarget.value)}
          />
          {item.dueAt !== null ? (
            <IconButton
              label={t('cardBack.checklists.item.clearDueLabel', { item: item.text })}
              size="sm"
              onClick={() => onSetDueDate(null)}
            >
              <X aria-hidden="true" className="size-4" />
            </IconButton>
          ) : null}
          {item.convertedCard === null ? (
            <IconButton
              label={t('cardBack.checklists.item.convertLabel', { item: item.text })}
              size="sm"
              onClick={onConvert}
            >
              <CreditCard aria-hidden="true" className="size-4" />
            </IconButton>
          ) : null}
          <IconButton
            label={t('cardBack.checklists.item.moveUp', { item: item.text })}
            size="sm"
            onClick={() => onMoveWithin('up')}
          >
            <ArrowUp aria-hidden="true" className="size-4" />
          </IconButton>
          <IconButton
            label={t('cardBack.checklists.item.moveDown', { item: item.text })}
            size="sm"
            onClick={() => onMoveWithin('down')}
          >
            <ArrowDown aria-hidden="true" className="size-4" />
          </IconButton>
          {checklistOptions.length > 1 ? (
            <select
              aria-label={t('cardBack.checklists.item.moveToChecklist', {
                item: item.text,
              })}
              className="h-7 max-w-32 rounded-card border border-border-strong bg-surface-raised px-2 text-sm text-ink"
              value={item.checklistId}
              onChange={(event) => onMoveToChecklist(event.currentTarget.value)}
            >
              {checklistOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.title}
                </option>
              ))}
            </select>
          ) : null}
          <IconButton
            label={t('cardBack.checklists.item.delete', { item: item.text })}
            size="sm"
            onClick={onDelete}
          >
            <Trash2 aria-hidden="true" className="size-4" />
          </IconButton>
        </div>
      ) : null}
    </li>
  );
}

interface MentionOptionData {
  readonly id: string;
  readonly label: string;
  readonly username: string;
  readonly profile: BoardMember;
}

function ChecklistItemTextEditor({
  t,
  text,
  checked,
  canEdit,
  people,
  currentUserId,
  onCommit,
}: {
  t: ChecklistTranslate;
  text: string;
  checked: boolean;
  canEdit: boolean;
  people: readonly BoardMember[];
  currentUserId: string | undefined;
  onCommit: (text: string) => Promise<void>;
}): ReactElement {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  const [error, setError] = useState<string | null>(null);
  const mentionQuery = activeMentionQuery(draft);
  const options = mentionOptions(people, mentionQuery);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  async function commit() {
    const next = draft.trim();
    if (next === '') {
      setDraft(text);
      setEditing(false);
      setError(null);
      return;
    }
    if (next === text) {
      setEditing(false);
      setError(null);
      return;
    }
    try {
      await onCommit(next);
      setEditing(false);
      setError(null);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : t('cardBack.checklists.item.saveFailed'),
      );
    }
  }

  function cancel() {
    setDraft(text);
    setError(null);
    setEditing(false);
  }

  if (!canEdit) {
    return (
      <span className={cn('block px-1 text-sm', checked && 'line-through')}>
        {renderChecklistText(t, text, people, currentUserId)}
      </span>
    );
  }

  if (!editing) {
    return (
      <span className="flex min-w-0 items-start gap-1">
        <span
          className={cn(
            'min-w-0 flex-1 rounded-card px-1 text-sm',
            checked && 'line-through',
          )}
        >
          {renderChecklistText(t, text, people, currentUserId)}
        </span>
        <IconButton
          label={t('cardBack.checklists.item.editLabel', { item: text })}
          size="sm"
          onClick={() => {
            setDraft(text);
            setError(null);
            setEditing(true);
          }}
        >
          <Pencil aria-hidden="true" className="size-4" />
        </IconButton>
      </span>
    );
  }

  return (
    <span
      className="block"
      onBlur={(event) => {
        const nextTarget = event.relatedTarget;
        if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget))
          return;
        void commit();
      }}
    >
      <textarea
        ref={textareaRef}
        rows={2}
        aria-label={t('cardBack.checklists.item.textLabel')}
        aria-invalid={error !== null ? true : undefined}
        value={draft}
        className="w-full rounded-card border border-border-strong bg-surface-raised px-1 text-sm text-ink focus:border-accent"
        onChange={(event) => setDraft(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
            cancel();
            return;
          }
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            void commit();
          }
        }}
      />
      {mentionQuery !== null ? (
        <Combobox
          label={t('cardBack.checklists.mention.itemLabel')}
          hideLabel
          autoFocus
          value={mentionQuery}
          onChange={(value) => setDraft(replaceActiveMention(draft, value, false))}
          minLength={2}
          leadingIcon={<AtSign aria-hidden="true" className="size-4" />}
          groups={[
            {
              id: 'board-members',
              label: t('cardBack.checklists.mention.group'),
              options,
            },
          ]}
          emptyState={
            mentionQuery.length < 2 ? (
              <EmptyState
                tone="neutral"
                size="inline"
                title={t('cardBack.checklists.mention.typeTwo')}
              />
            ) : (
              <EmptyState
                tone="neutral"
                size="inline"
                title={t('cardBack.checklists.mention.noMatch')}
              />
            )
          }
          onActivate={(option) => {
            setDraft(replaceActiveMention(draft, option.username, true));
            window.setTimeout(() => textareaRef.current?.focus(), 0);
          }}
          renderOption={(option) => <MentionOption option={option} />}
        />
      ) : null}
      <span className="mt-1 flex items-center gap-1">
        <Button size="sm" variant="secondary" onClick={() => void commit()}>
          {t('cardBack.checklists.item.save')}
        </Button>
        <Button size="sm" variant="quiet" onClick={cancel}>
          {t('cardBack.checklists.cancel')}
        </Button>
      </span>
      {error !== null ? (
        <span
          role="alert"
          className="mt-1 block rounded bg-surface-raised px-1 text-xs text-bad"
        >
          {error}
        </span>
      ) : null}
    </span>
  );
}

function MentionOption({ option }: { option: MentionOptionData }): ReactElement {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <Avatar profile={option.profile} size="xs" decorative />
      <span className="flex min-w-0 flex-col">
        <span className="truncate">{option.label}</span>
        <span className="truncate text-xs text-ink-muted">@{option.username}</span>
      </span>
    </span>
  );
}

function renderChecklistText(
  t: ChecklistTranslate,
  text: string,
  people: readonly BoardMember[],
  currentUserId: string | undefined,
): ReactElement {
  return <>{renderChecklistInline(t, text, people, currentUserId, 'root')}</>;
}

type ChecklistInlineToken =
  | { readonly kind: 'break'; readonly index: number; readonly length: number }
  | {
      readonly kind: 'code' | 'strong' | 'emphasis' | 'strike';
      readonly index: number;
      readonly length: number;
      readonly text: string;
    }
  | {
      readonly kind: 'link';
      readonly index: number;
      readonly length: number;
      readonly text: string;
      readonly href: string;
    };

function renderChecklistInline(
  t: ChecklistTranslate,
  text: string,
  people: readonly BoardMember[],
  currentUserId: string | undefined,
  keyPrefix: string,
): ReactNode[] {
  if (text === '') return [];

  const token = findChecklistInlineToken(text);
  if (token === null) return renderMentionText(t, text, people, currentUserId, keyPrefix);

  const before = text.slice(0, token.index);
  const after = text.slice(token.index + token.length);
  const rendered: ReactNode[] = [
    ...renderMentionText(t, before, people, currentUserId, `${keyPrefix}-before`),
  ];

  switch (token.kind) {
    case 'break':
      rendered.push(<br key={`${keyPrefix}-br-${token.index}`} />);
      break;
    case 'code':
      rendered.push(
        <code
          key={`${keyPrefix}-code-${token.index}`}
          className="rounded-card bg-surface px-1 font-mono text-[0.85em]"
        >
          {token.text}
        </code>,
      );
      break;
    case 'strong':
      rendered.push(
        <strong key={`${keyPrefix}-strong-${token.index}`}>
          {renderChecklistInline(
            t,
            token.text,
            people,
            currentUserId,
            `${keyPrefix}-strong-${token.index}`,
          )}
        </strong>,
      );
      break;
    case 'emphasis':
      rendered.push(
        <em key={`${keyPrefix}-em-${token.index}`}>
          {renderChecklistInline(
            t,
            token.text,
            people,
            currentUserId,
            `${keyPrefix}-em-${token.index}`,
          )}
        </em>,
      );
      break;
    case 'strike':
      rendered.push(
        <del key={`${keyPrefix}-del-${token.index}`}>
          {renderChecklistInline(
            t,
            token.text,
            people,
            currentUserId,
            `${keyPrefix}-del-${token.index}`,
          )}
        </del>,
      );
      break;
    case 'link': {
      const href = safeChecklistHref(token.href);
      rendered.push(
        href === null ? (
          <span key={`${keyPrefix}-link-${token.index}`}>
            {renderChecklistInline(
              t,
              token.text,
              people,
              currentUserId,
              `${keyPrefix}-link-${token.index}`,
            )}
          </span>
        ) : (
          <a
            key={`${keyPrefix}-link-${token.index}`}
            href={href}
            target={isExternalChecklistHref(href) ? '_blank' : undefined}
            rel={isExternalChecklistHref(href) ? 'noopener noreferrer' : undefined}
            className="font-medium text-accent underline"
          >
            {renderChecklistInline(
              t,
              token.text,
              people,
              currentUserId,
              `${keyPrefix}-link-${token.index}`,
            )}
          </a>
        ),
      );
      break;
    }
  }

  rendered.push(
    ...renderChecklistInline(t, after, people, currentUserId, `${keyPrefix}-after`),
  );
  return rendered;
}

function renderMentionText(
  t: ChecklistTranslate,
  text: string,
  people: readonly BoardMember[],
  currentUserId: string | undefined,
  keyPrefix: string,
): ReactNode[] {
  if (text === '') return [];
  return mentionSegments(text, people).map((segment, index) => {
    if (segment.kind === 'text') {
      return <span key={`${keyPrefix}-text-${index}`}>{segment.text}</span>;
    }
    const self = currentUserId !== undefined && segment.member.id === currentUserId;
    return (
      <span
        key={`${keyPrefix}-mention-${index}-${segment.member.id}`}
        className={cn(
          'inline-flex items-center rounded-card border px-1 font-medium',
          self
            ? 'border-accent bg-accent/10 text-accent'
            : 'border-border-strong bg-surface text-ink',
        )}
        aria-label={t('primitives.markdown.mention', {
          name: profileName(segment.member),
        })}
      >
        {segment.text}
      </span>
    );
  });
}

function findChecklistInlineToken(text: string): ChecklistInlineToken | null {
  const candidates: ChecklistInlineToken[] = [];
  const lineBreak = text.indexOf('\n');
  if (lineBreak !== -1) {
    candidates.push({ kind: 'break', index: lineBreak, length: 1 });
  }

  const code = /`([^`\n]+)`/.exec(text);
  if (code?.[1] !== undefined) {
    candidates.push({
      kind: 'code',
      index: code.index,
      length: code[0].length,
      text: code[1],
    });
  }

  const link = /\[([^\]\n]+)\]\(([^()\s]+)\)/.exec(text);
  if (link?.[1] !== undefined && link[2] !== undefined) {
    candidates.push({
      kind: 'link',
      index: link.index,
      length: link[0].length,
      text: link[1],
      href: link[2],
    });
  }

  const strong = /\*\*([^\n]+?)\*\*|__([^\n]+?)__/.exec(text);
  if (strong !== null) {
    candidates.push({
      kind: 'strong',
      index: strong.index,
      length: strong[0].length,
      text: strong[1] ?? strong[2] ?? '',
    });
  }

  const strike = /~~([^\n]+?)~~/.exec(text);
  if (strike?.[1] !== undefined) {
    candidates.push({
      kind: 'strike',
      index: strike.index,
      length: strike[0].length,
      text: strike[1],
    });
  }

  const emphasis = /(^|[^*])\*([^*\n]+)\*(?!\*)|(^|[^_])_([^_\n]+)_(?!_)/.exec(text);
  if (emphasis !== null) {
    const prefix = emphasis[1] ?? emphasis[3] ?? '';
    candidates.push({
      kind: 'emphasis',
      index: emphasis.index + prefix.length,
      length: emphasis[0].length - prefix.length,
      text: emphasis[2] ?? emphasis[4] ?? '',
    });
  }

  if (candidates.length === 0) return null;
  return candidates.sort((left, right) => left.index - right.index)[0] ?? null;
}

function safeChecklistHref(href: string): string | null {
  const trimmed = href.trim();
  if (/^(https?:|mailto:|tel:)/i.test(trimmed) || /^(\/|#|\.\.?\/)/.test(trimmed)) {
    return trimmed;
  }
  return null;
}

function isExternalChecklistHref(href: string): boolean {
  return /^(https?:|mailto:|tel:)/i.test(href);
}

function mentionOptions(
  people: readonly BoardMember[],
  query: string | null,
): MentionOptionData[] {
  if (query === null) return [];
  const needle = query.toLocaleLowerCase();
  return people
    .filter((person) =>
      `${person.username} ${profileName(person)}`.toLocaleLowerCase().includes(needle),
    )
    .slice(0, 8)
    .map((person) => ({
      id: person.id,
      label: profileName(person),
      username: person.username,
      profile: person,
    }));
}

function activeMentionQuery(body: string): string | null {
  const match = /(?:^|\s)@([A-Za-z0-9._-]*)$/.exec(body);
  return match?.[1] ?? null;
}

function replaceActiveMention(body: string, value: string, commit: boolean): string {
  return body.replace(/(^|\s)@([A-Za-z0-9._-]*)$/, (_match, prefix: string) => {
    const suffix = commit ? ' ' : '';
    return `${prefix}@${value}${suffix}`;
  });
}

function dueStateClasses(state: ReturnType<typeof dueStatus>): string {
  switch (state) {
    case 'complete':
      return 'border-ok text-ok';
    case 'due-soon':
      return 'border-warn text-warn';
    case 'overdue':
    case 'long-overdue':
      return 'border-bad text-bad';
    case 'neutral':
    case 'none':
      return 'border-border text-ink-muted';
  }
}

function boardCards(lists: readonly BoardFaceList[]): BoardFaceList['cards'] {
  return lists.flatMap((list) => list.cards);
}

type MoveDirection = 'up' | 'down';

interface ChecklistMoveOption {
  readonly id: string;
  readonly title: string;
}

interface ChecklistMoveNeighbours {
  readonly previousChecklistId: string | null;
  readonly nextChecklistId: string | null;
}

interface ChecklistItemMoveNeighbours {
  readonly previousItemId: string | null;
  readonly nextItemId: string | null;
}

function checklistMoveNeighbours(
  checklists: readonly Checklist[],
  checklistId: string,
  direction: MoveDirection,
): ChecklistMoveNeighbours | null {
  const index = checklists.findIndex((checklist) => checklist.id === checklistId);
  if (index === -1) return null;
  const targetIndex = direction === 'up' ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= checklists.length) return null;
  if (direction === 'up') {
    return {
      previousChecklistId: checklists[targetIndex - 1]?.id ?? null,
      nextChecklistId: checklists[targetIndex]?.id ?? null,
    };
  }
  return {
    previousChecklistId: checklists[targetIndex]?.id ?? null,
    nextChecklistId: checklists[targetIndex + 1]?.id ?? null,
  };
}

function checklistItemMoveNeighbours(
  items: readonly ChecklistItem[],
  itemId: string,
  direction: MoveDirection,
): ChecklistItemMoveNeighbours | null {
  const index = items.findIndex((item) => item.id === itemId);
  if (index === -1) return null;
  const targetIndex = direction === 'up' ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= items.length) return null;
  if (direction === 'up') {
    return {
      previousItemId: items[targetIndex - 1]?.id ?? null,
      nextItemId: items[targetIndex]?.id ?? null,
    };
  }
  return {
    previousItemId: items[targetIndex]?.id ?? null,
    nextItemId: items[targetIndex + 1]?.id ?? null,
  };
}
